/**
 * Rivers, derived from the ground rather than drawn on it.
 *
 * The board had no watercourses at all. `LinkKind.River` marks a *border* as a
 * ford — a fact about how expensive it is to march across a line — and the chart
 * drew a couple of ticks on that line, but there was no water anywhere on the
 * map. On a world with mountains, rain and a coastline that is a conspicuous
 * absence: rivers are the one feature a reader uses to tell which way the land
 * falls.
 *
 * ## Where they come from
 *
 * Not from noise, and not hand-placed. `RASTER.height` is a real elevation
 * field, so this runs the same thing a GIS runs on a DEM:
 *
 *   1. **D8 flow direction.** Every land cell drains to the lowest of its eight
 *      neighbours. Sea counts as lower than any land, so the coast is where
 *      flow leaves the board.
 *   2. **Flow accumulation.** Walk the cells from high to low, handing each
 *      one's accumulated water to its downstream neighbour. Because a cell is
 *      only visited after everything above it, one pass in sorted order is
 *      exact — no iteration, no convergence check.
 *   3. **A threshold.** A cell carrying more than `SOURCE` cells' worth of
 *      water is a river. That single number decides the drainage density of the
 *      whole world, which is why it is a named constant with an argument
 *      attached rather than a literal.
 *
 * What falls out of that is worth more than the rivers themselves: the network
 * is *correct with respect to the terrain*. Rivers rise in the mountains the
 * hillshade is drawing, run down the valleys it is shading, meet each other at
 * junctions and reach the sea. Nothing has to be kept in step by hand, and a
 * change to the world generator moves the rivers with it.
 *
 * ## Why vector and not another baked layer
 *
 * The relief, the fabric and the shelf are rasters because they are a value per
 * cell over the whole board. A river is a *line* — a few thousand of them, each
 * a handful of points — and a line drawn as a path stays sharp at every zoom
 * where a baked one goes soft. It is also the one thing on this chart that
 * genuinely wants a varying width: a stream and a great river are the same
 * feature at two scales, and stroke-width says so for free.
 */

import { RASTER, WORLD } from '../data/realm.ts';
import { warp } from '../data/cartography.ts';
import type { Pt } from '../data/cartography.ts';
import { filledHeights } from './relief.ts';
import { shoreCliff } from './shore.ts';

/**
 * How many cells must drain through a point before it is drawn as water.
 *
 * This is the drainage density of the world in one number. Too low and every
 * hillside grows a creek until the map is a net; too high and a continent has
 * four rivers on it. 55 cells is about 220 square map units of catchment, which
 * on this board puts a stream in most valleys and leaves the dry middle of the
 * eastern desert dry — which is the test, because that desert exists *because*
 * the rain model says no water gets there, and a river running through it would
 * be the map contradicting the simulation.
 */
const SOURCE = 55;

/** Accumulation at which a watercourse is drawn at full width. */
const GREAT = 2600;

/**
 * What a river does when it finally gets there.
 *
 * The routing pass took rivers reaching the sea from 25.5% to 100% and then
 * drew every arrival identically: a stroke that stops at the shoreline, the
 * same width as it was ten miles inland. A river mouth is the one place on a
 * chart where the water and the land are both doing something, and it is the
 * thing the eye uses to decide whether a drainage network was derived or drawn.
 *
 * Two features, and both are gated on facts the routing already has.
 *
 * **An estuary** — the last few cells restated at increasing width — belongs to
 * every mouth, because every river widens as it loses its gradient. Measured on
 * this board there are **93 of them**.
 *
 * **A delta** belongs only to a big river arriving on a low shore, because that
 * is the only place one forms: sediment needs somewhere flat to be dropped, and
 * a river that meets a cliff has a rock coast and no delta whatever it carries.
 * `shoreCliff` is the same field the coast is drawn from, so the two cannot
 * disagree. Flow at the mouth runs median 177, p90 996, max 6,378, and **11
 * mouths carry more than `DELTA_FLOW`, 9 of them onto a low shore** — so this
 * is a handful of great rivers on the whole board rather than a decoration on
 * every stream, which is what it should be.
 */
const DELTA_FLOW = 800;
/** Above this cliffness the shore is rock, and rock does not take a delta. */
const DELTA_SHORE = 0.35;
/** How far a distributary reaches past the mouth, in map units. */
const DELTA_REACH = 6.5;

let cached = '';

/**
 * The river network as SVG paths.
 *
 * Computed once and kept: it is a function of the world's elevation, which does
 * not change after generation.
 */
export function riverPaths(): string {
  if (cached !== '') return cached;
  const { cols, rows, owner, originX, originY, cell } = RASTER;
  const n = cols * rows;

  // The depressions, filled — the standard preprocessing that gave this network
  // its 100% arrival rate. It lives in `relief.ts` now, beside the other fields,
  // because the *lakes* on the chart are the same computation read the other way
  // round: the water a basin holds is exactly what the fill had to add. Two
  // priority floods would have been two answers about the same ground.
  const filled = filledHeights();

  /**
   * Downstream neighbour per cell, or −1 for "leaves the board here".
   *
   * Routed on `filled`, so a cell always has somewhere lower to send its water
   * unless it is on the coast, where the sea takes it.
   */
  const down = new Int32Array(n).fill(-1);
  const order: number[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const k = row * cols + col;
      if (owner[k] < 0) continue;
      order.push(k);
      let best = -1;
      let bestH = filled[k];
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const cx = col + dx;
          const cy = row + dy;
          if (cx < 0 || cy < 0 || cx >= cols || cy >= rows) continue;
          const j = cy * cols + cx;
          // The sea is the bottom of every catchment. Marked with −1 rather
          // than routed into, so the trace knows to stop at the shore.
          if (owner[j] < 0) {
            best = -2;
            bestH = -Infinity;
            continue;
          }
          if (filled[j] < bestH) {
            bestH = filled[j];
            best = j;
          }
        }
      }
      down[k] = best;
    }
  }

  // High to low on the *filled* field, so every cell is handed its water before
  // it passes any on. Sorting on the raw heights here would break the guarantee:
  // a cell inside a filled basin can sit below its own downstream neighbour in
  // the real ground, and would then pass water to a cell already processed.
  order.sort((a, b) => filled[b] - filled[a]);
  const flow = new Float32Array(n).fill(1);
  for (const k of order) {
    const j = down[k];
    if (j >= 0) flow[j] += flow[k];
  }

  /**
   * Trace each course from its source to the sea.
   *
   * A course starts at a cell that is above the threshold and whose *upstream*
   * is not — the spring — and follows `down` until the water leaves the board
   * or rejoins a course already drawn. Tracking which cells have been walked is
   * what stops a trunk river being redrawn once per tributary: on this board
   * that turned five thousand segments into about nine hundred courses.
   */
  const isRiver = (k: number): boolean => owner[k] >= 0 && flow[k] >= SOURCE;
  const walked = new Uint8Array(n);
  const upstream = new Int32Array(n);
  for (const k of order) {
    const j = down[k];
    if (j >= 0 && isRiver(k)) upstream[j]++;
  }

  // The same field the coast is drawn from, so a delta and a cliff cannot
  // disagree about the same stretch of shore.
  const cliffField = shoreCliff();

  /**
   * Where a cell of the network is *drawn* — and it is not where the cell is.
   *
   * **A river reached the sea two to six map units from where the sea is.**
   * Every line on this chart is displaced at the end by `cartography.ts`'s plane
   * warp; the river network was the one vector on the sheet that was not, so it
   * was drawn in raster coordinates while the coastline it runs into was drawn
   * in warped ones. Along a stretch where the warp pushes the shore seaward, a
   * course stopped short and ended in dry ground; along the next it ran out past
   * the beach and its delta fanned into open water. That is the whole of why the
   * mouths never quite landed, and it was invisible in the source because both
   * halves were individually correct.
   *
   * The rasters solve the same disagreement from the other end — the strand and
   * the hillshade are sampled through the warp *backwards*, see `planeSource` —
   * but a vector cannot be resampled, so a vector gets warped. Two directions,
   * one coordinate system.
   *
   * Cached per cell: a trunk river and each of its tributaries walk the same
   * cells, and `warp` is six noise lookups.
   */
  const wx = new Map<number, Pt>();
  const at = (k: number): Pt => {
    const hit = wx.get(k);
    if (hit) return hit;
    const p = warp({
      x: originX + (k % cols) * cell + cell / 2,
      y: originY + ((k / cols) | 0) * cell + cell / 2,
    });
    wx.set(k, p);
    return p;
  };
  const out: string[] = [];
  const px = (k: number): number => at(k).x;
  const py = (k: number): number => at(k).y;

  for (const start of order) {
    if (!isRiver(start) || walked[start] || upstream[start] > 0) continue;
    const pts: number[] = [start];
    let k = start;
    let guard = 0;
    while (guard++ < 4096) {
      walked[k] = 1;
      const j = down[k];
      if (j < 0) break;
      pts.push(j);
      if (walked[j]) break;
      k = j;
    }
    if (pts.length < 3) continue;

    /**
     * One path per course, split where its width changes materially.
     *
     * A river that gathers three tributaries on its way down is genuinely
     * wider at the bottom than the top, and a single stroke cannot say that.
     * Rather than a path per cell — which is tens of thousands of elements —
     * the course is cut into runs of similar flow, so a stream that stays a
     * stream is one path and a trunk river is three or four.
     */
    let runStart = 0;
    /**
     * Width in **screen pixels**, because the stroke is `non-scaling-stroke`.
     *
     * That was the bug in the first cut: the widths were written in map units
     * — 0.28 to 1.9, which is right for a path whose stroke scales with the
     * view — while the CSS pinned the stroke to the glass. A 0.28-pixel line
     * is not a thin river, it is no river, and the whole network rendered as a
     * ghost. Non-scaling is the correct choice, for the reason the lettering
     * and the roads make it: a river should be the same weight on the glass at
     * every zoom. So the numbers move to the glass's units instead.
     *
     * Square root of the flow, not the flow: discharge grows with catchment far
     * faster than a drawn line may, and a linear map would put a trunk river at
     * forty times the width of its own headwaters.
     */
    const widthOf = (f: number): number =>
      Math.max(0.7, Math.min(3.4, 0.7 + Math.sqrt(f / GREAT) * 2.9));
    let runW = widthOf(flow[pts[0]]);
    for (let i = 1; i <= pts.length; i++) {
      const w = i < pts.length ? widthOf(flow[pts[i]]) : runW;
      const last = i === pts.length;
      if (!last && Math.abs(w - runW) < 0.4) continue;
      const seg = pts.slice(runStart, Math.min(i + 1, pts.length));
      if (seg.length >= 2) {
        out.push(
          `<path class="wm-river" stroke-width="${runW.toFixed(2)}" d="${smooth(seg, px, py)}" />`,
        );
      }
      runStart = i;
      runW = w;
    }

    /**
     * The mouth.
     *
     * A course that ran off the end of `down` is one that reached the sea; a
     * course that stopped because it met another is a tributary and gets
     * nothing. That single test is the whole gate, and it is the same one the
     * routing pass measured its 100% with.
     */
    const mouth = pts[pts.length - 1];
    if (down[mouth] < 0 && pts.length >= 4) {
      const f = flow[mouth];
      const w0 = widthOf(f);
      // Widest last, so it lands on top of the narrower restatements and the
      // three together read as a funnel rather than as three lines.
      for (const step of [
        { back: 6, m: 1.16 },
        { back: 4, m: 1.42 },
        { back: 2, m: 1.74 },
      ]) {
        const seg = pts.slice(Math.max(0, pts.length - step.back));
        if (seg.length < 2) continue;
        out.push(
          `<path class="wm-river wm-estuary" stroke-width="${(w0 * step.m).toFixed(2)}" ` +
            `d="${smooth(seg, px, py)}" />`,
        );
      }

      if (f >= DELTA_FLOW && cliffField[mouth] < DELTA_SHORE) {
        /**
         * Distributaries, fanned off the last bearing the river held.
         *
         * Deterministic in every part: the bearing is the course's own last two
         * cells and the spread is fixed, so a delta is a function of the terrain
         * exactly as the river above it is. No dice, because this file is not
         * allowed any and would not want them — a delta that moved between
         * reloads would be the one thing on the chart that was not about the
         * ground.
         */
        const ax = px(mouth) - px(pts[pts.length - 3]);
        const ay = py(mouth) - py(pts[pts.length - 3]);
        const al = Math.hypot(ax, ay) || 1;
        const bx = ax / al;
        const by = ay / al;
        const arm = (turn: number, reach: number): string => {
          const c1 = Math.cos(turn);
          const s1 = Math.sin(turn);
          const dx = bx * c1 - by * s1;
          const dy = bx * s1 + by * c1;
          const x0 = px(mouth);
          const y0 = py(mouth);
          // A bend halfway along, so a distributary is a channel and not a spoke.
          const mx = x0 + dx * reach * 0.55 - dy * reach * 0.12;
          const my = y0 + dy * reach * 0.55 + dx * reach * 0.12;
          return (
            `M${x0.toFixed(2)} ${y0.toFixed(2)}` +
            `Q${mx.toFixed(2)} ${my.toFixed(2)} ` +
            `${(x0 + dx * reach).toFixed(2)} ${(y0 + dy * reach).toFixed(2)}`
          );
        };
        const dw = (w0 * 0.52).toFixed(2);
        out.push(
          `<path class="wm-river wm-distrib" stroke-width="${dw}" d="${arm(0.52, DELTA_REACH)}" />`,
          `<path class="wm-river wm-distrib" stroke-width="${dw}" d="${arm(-0.52, DELTA_REACH * 0.86)}" />`,
          `<path class="wm-river wm-distrib" stroke-width="${dw}" d="${arm(0.06, DELTA_REACH * 0.62)}" />`,
        );
      }
    }
  }

  cached = `<g class="wm-rivers">${out.join('')}</g>`;
  return cached;
}

/**
 * A course as a smoothed path.
 *
 * D8 flow can only leave a cell in one of eight directions, so a raw trace is a
 * staircase of 45° steps — unmistakably a grid artefact and the thing that makes
 * a derived river network look derived. Every point is replaced by the midpoint
 * of its neighbours and the corners joined with quadratics through the
 * originals, which is a Chaikin-flavoured smoothing: it costs one pass and it
 * turns the staircase into the meander a river actually has.
 */
function smooth(cells: number[], px: (k: number) => number, py: (k: number) => number): string {
  const xs = cells.map(px);
  const ys = cells.map(py);
  let d = `M${xs[0].toFixed(2)} ${ys[0].toFixed(2)}`;
  for (let i = 1; i < xs.length - 1; i++) {
    const mx = (xs[i] + xs[i + 1]) / 2;
    const my = (ys[i] + ys[i + 1]) / 2;
    d += `Q${xs[i].toFixed(2)} ${ys[i].toFixed(2)} ${mx.toFixed(2)} ${my.toFixed(2)}`;
  }
  d += `L${xs[xs.length - 1].toFixed(2)} ${ys[ys.length - 1].toFixed(2)}`;
  return d;
}

/** Where the network sits in world units, for anything that needs the extent. */
export const RIVER_EXTENT = {
  x: WORLD.originX,
  y: WORLD.originY,
  w: WORLD.w,
  h: WORLD.h,
};
